/**
 * Utilities for defining named slots using compound components.  This allows
 * the parent component to define it's slots, but then the children can be
 * supplied in any order as regular children.
 *
 * Slots are defined by strings.  Any string name can be used, and any number of
 * components can share the same slot name.
 *
 * @example
 * const TwoColumn = ({children)) =>
 *  <div>
 *    <div className="left">{findAllForSlot('left', children)}</div>
 *    <div className="right">{findFirstForSlot('right', children)}</div>
 *  </div>;
 *
 *  TwoColumn.Left = defineSlotComponent('left', (props) => {...};
 *  TwoColumn.Right = defineSlotComponent('right', (props) => {...};
 *
 * const Page = () =>
 *   <TwoColumn>
 *    <TwoColumn.Left>Left Item 1</TwoColumn.Left>
 *    <TwoColumn.Left>Left Item 2</TwoColumn.Left>
 *    <TwoColumn.Right>Right Content</TwoColumn.Right>
 *  </TwoColumn>
 */
import * as React from 'react';

type SlotComponent<P> = React.FC<P> & {
  SLOT_NAME?: string;
};

/** Defines a component as belonging to a given slot. */
export const defineSlotComponent = <P>(name: string, component: React.FC<P>): SlotComponent<P> => {
  const slotComponent = component as SlotComponent<P>;
  slotComponent.SLOT_NAME = name;
  return slotComponent;
};

/** Finds the first child for a named slot. */
export const findFirstForSlot = (
  name: string,
  children: React.ReactNode,
): React.ReactChild | React.ReactFragment | React.ReactPortal | undefined =>
  React.Children.toArray(children).find(child =>
    React.isValidElement(child) && (child.type as SlotComponent<any>).SLOT_NAME === name,
  );

/** Finds all children for a set of slots - this will retain the original order of children */
export const findAllForSlots = (
  names: string[],
  children: React.ReactNode,
): Array<React.ReactChild | React.ReactFragment | React.ReactPortal> =>
  React.Children.toArray(children).filter(child =>
    React.isValidElement(child) && names.includes(((child.type as SlotComponent<any>).SLOT_NAME) || ''),
  );

/** Finds all children for a named slot. */
export const findAllForSlot = (
  name: string,
  children: React.ReactNode,
): Array<React.ReactChild | React.ReactFragment | React.ReactPortal> =>
  findAllForSlots([name], children);
